import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formService } from '../services/formService';

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '2rem',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    padding: '3rem 2.5rem',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  logo: {
    width: '120px',
    marginBottom: '1.5rem',
  },
  icon: {
    fontSize: '3.5rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: '1rem',
  },
  message: {
    fontSize: '1rem',
    color: '#555',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
  },
  timeBox: {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  timeLabel: {
    fontSize: '0.85rem',
    color: '#888',
    marginBottom: '0.3rem',
  },
  timeValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#2c3e50',
  },
  hint: {
    fontSize: '0.85rem',
    color: '#999',
    fontStyle: 'italic',
  },
  statusDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '6px',
    animation: 'pulse 2s infinite',
  },
};

function formatDateTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }) + ' (HKT)';
}

export default function MaintenancePage() {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [checking, setChecking] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        setChecking(true);
        const status = await formService.getMaintenanceStatus();
        setSchedule(status);

        // Admin bypass: server checks JWT cookie and returns isAdmin
        if (status.isAdmin) {
          navigate('/admin/templates', { replace: true });
          return;
        }

        // If maintenance is over, redirect back to home
        if (!status.scheduled || (!status.isActive && status.minutesUntilStart === 0)) {
          navigate('/', { replace: true });
        }
      } catch {
        // Backend is down — stay on this page, Nginx static fallback handles it
      } finally {
        setChecking(false);
      }
    };

    checkStatus();
    intervalRef.current = setInterval(checkStatus, 30000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [navigate]);

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
      <div style={styles.card}>
        <img
          src="/images/logo.png"
          alt="OT&P Logo"
          style={styles.logo}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <h1 style={styles.title}>
          System Maintenance In Progress
        </h1>
        <p style={styles.message}>
          {schedule?.message || 'System maintenance in progress. Please try again later.'}
        </p>

        {schedule?.endTime && (
          <div style={styles.timeBox}>
            <div style={styles.timeLabel}>Estimated Completion Time</div>
            <div style={styles.timeValue}>{formatDateTime(schedule.endTime)}</div>
          </div>
        )}

        <p style={styles.hint}>
          <span style={{ ...styles.statusDot, background: checking ? '#3498db' : '#2ecc71' }} />
          This page will automatically redirect when maintenance is complete.
        </p>
      </div>
    </div>
  );
}
